import React, { useMemo } from "react";
import { Field, useForm } from "react-final-form";
import clsx from "clsx";
import { Theme } from "@material-ui/core/styles";
import InputLabel from "@material-ui/core/InputLabel";
import makeStyles from "@material-ui/core/styles/makeStyles";
import Typography from "@material-ui/core/Typography";
import { Grid } from "@material-ui/core";
import WeduCheckbox from "../../../../components/WeduCheckbox/WeduCheckbox";
import InfoIcon from "../../../../components/InfoIcon";
import { REQUIRED_DOCS } from "../../constants";
import WeduTooltip from "../../../../components/WeduTooltip/intex";

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    paddingBottom: 3,
  },
  label: {
    marginBottom: 4,
    marginLeft: 4,
    fontSize: 14,
    fontWeight: 400,
    color: "#a1a4ac",
    lineHeight: 1,
  },
  helperText: {
    height: 0,
    fontSize: 14,
    fontWeight: 500,
    textAlign: "right",
    color: "#a1a4ac",
  },
  hasError: {
    color: theme.palette.error.main,
  },
  headerContainerError: {
    borderTopColor: `${theme.palette.error.main} !important`,
    borderLeftColor: `${theme.palette.error.main} !important`,
    borderRightColor: `${theme.palette.error.main} !important`,
  },
  checkboxContainerError: {
    borderBottomColor: `${theme.palette.error.main} !important`,
    borderLeftColor: `${theme.palette.error.main} !important`,
    borderRightColor: `${theme.palette.error.main} !important`,
  },
  headerContainer: {
    border: "solid 1px #dbdbdb",
    borderTopLeftRadius: 15,
    borderTopRightRadius: 15,
    height: 44,
    padding: "0 16px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  checkboxContainer: {
    borderLeft: "solid 1px #dbdbdb",
    borderRight: "solid 1px #dbdbdb",
    borderBottom: "solid 1px #dbdbdb",
    backgroundColor: "#f5f6f7",
    borderBottomLeftRadius: 15,
    borderBottomRightRadius: 15,
    padding: "8px 0 8px 16px",
  },
  span: {
    fonFamily: "Roboto",
    fontSize: 14,
    fontWeight: "normal",
    color: "#bcbcbc",
  },
}));

const RequiredDocsMultiSelect = (props) => {
  const classes = useStyles();

  const { change } = useForm();

  const {
    fields: { value, name },
    meta: { submitError, invalid, submitFailed, dirtySinceLastSubmit },
    infoText,
  } = props;

  const mergedRequiredDocs = useMemo(
    () =>
      REQUIRED_DOCS.map((mainDoc) => {
        const receivedMainDoc = value.find(
          (receivedDoc) => receivedDoc.name === mainDoc.name,
        );

        return receivedMainDoc || mainDoc;
      }),
    [],
  );

  const hasError = useMemo(
    () => invalid && submitFailed && !dirtySinceLastSubmit,
    [invalid, dirtySinceLastSubmit, submitFailed],
  );

  const selectedDocsCount = value?.length || 0;
  const allDocsCount = REQUIRED_DOCS.length;

  const selectAllDeselectAllHandler = () => {
    if (selectedDocsCount === allDocsCount) {
      change(name, []);
      return;
    }

    change(name, mergedRequiredDocs);
  };

  return (
    <>
      <InputLabel className={classes.label}>
        Required documents
        <Typography
          className={clsx(classes.label, hasError && classes.hasError)}
          component={"span"}
          variant={"subtitle2"}
        >
          *
        </Typography>
        {infoText && <InfoIcon text={infoText} />}
      </InputLabel>
      <div className={classes.root}>
        <div
          className={clsx(
            classes.headerContainer,
            hasError && classes.headerContainerError,
          )}
        >
          <span className={classes.span}>
            {selectedDocsCount
              ? `${selectedDocsCount} of ${allDocsCount} Selected`
              : "Nothing  Selected"}
          </span>
          <Typography
            className={"wedu-link"}
            onClick={selectAllDeselectAllHandler}
          >
            {selectedDocsCount === allDocsCount ? "Deselect All" : "Select All"}
          </Typography>
        </div>
        <div
          className={clsx(
            classes.checkboxContainer,
            hasError && classes.checkboxContainerError,
          )}
        >
          <Grid container>
            {mergedRequiredDocs.map((doc) => (
              <Grid key={doc.name} item xs={12} sm={3}>
                {doc.longName === doc.shortName ? (
                  <Field
                    value={doc}
                    label={doc.shortName}
                    type="checkbox"
                    name={name}
                    component={WeduCheckbox}
                  />
                ) : (
                  <WeduTooltip title={doc.longName}>
                    <div>
                      <Field
                        value={doc}
                        label={doc.shortName}
                        type="checkbox"
                        name={name}
                        component={WeduCheckbox}
                      />
                    </div>
                  </WeduTooltip>
                )}
              </Grid>
            ))}
          </Grid>
        </div>
      </div>
      {hasError && (
        <InputLabel
          className={clsx(classes.helperText, hasError && classes.hasError)}
        >
          {submitError}
        </InputLabel>
      )}
    </>
  );
};

export default React.memo(RequiredDocsMultiSelect);
